import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/useAuth';
import { formatUserFacingError } from '@/utils/userFacingError';
import { sanitizeNextPath } from '@/utils/sanitizeNextPath';

export function AuthPage() {
  const { isAuthenticated, isLoadingAuth, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const nextPath = sanitizeNextPath(
    new URLSearchParams(location.search).get('next') ??
      (location.state as { from?: { pathname: string } } | null)?.from?.pathname,
  );

  if (!isLoadingAuth && isAuthenticated) {
    return <Navigate to={nextPath} replace />;
  }

  async function onGoogleSignIn() {
    setFormError(null);
    setIsSigningIn(true);

    try {
      await signInWithGoogle();
      navigate(nextPath, { replace: true });
    } catch (error) {
      setFormError(formatUserFacingError(error, 'Unable to sign in. Please try again.'));
    } finally {
      setIsSigningIn(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="auth-orb auth-orb-primary" />
      <div className="auth-orb auth-orb-mint" />
      <div className="auth-orb auth-orb-blush" />

      <div className="auth-frame">
        <section className="auth-form-panel" aria-labelledby="auth-title">
          <div className="auth-mobile-brand">
            <div className="auth-logo-mark auth-logo-mark-text">
              <span>RN</span>
            </div>
            <div>
              <p className="auth-mobile-name">Rodeo Nails</p>
              <p className="auth-eyebrow">Staff Portal</p>
            </div>
          </div>

          <div className="auth-card">
            <div className="auth-card-header">
              <div>
                <h2 id="auth-title">Welcome back</h2>
                <p>Sign in with Google to access the Rodeo Nails management dashboard.</p>
              </div>
            </div>

            <button
              className="auth-google-button"
              disabled={isSigningIn}
              onClick={onGoogleSignIn}
              type="button"
            >
              <span>G</span>
              {isSigningIn ? (
                <>
                  <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  Continue with Google
                </>
              ) : (
                'Continue with Google'
              )}
            </button>

            {formError && <p className="auth-form-error">{formError}</p>}
          </div>
        </section>
      </div>
    </main>
  );
}
